import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Business as CompaniesIcon
} from '@mui/icons-material';
import { companyService } from '../services/companyService';
import UserDisplay from '../components/UserDisplay';


const Companies = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State for companies data
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // State for table functionality
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('companyName');
  const [order, setOrder] = useState('asc');

  // State for search filters
  const [filters, setFilters] = React.useState({
    companyName: '',
    mainPhone: '',
    email: '',
    createdByEmail: '',
    createdDate: ''
  });

  // State for delete confirmation
  const [deleteDialog, setDeleteDialog] = useState({ open: false, company: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  // Auto-refresh functionality
  useEffect(() => {
    // Refresh data when window regains focus (user switches back to tab)
    const handleFocus = () => {
      loadCompanies(true); // Force refresh
    };

    // Refresh data every 2 minutes when tab is active
    const refreshInterval = setInterval(() => {
      if (!document.hidden) {
        loadCompanies(false); // Background refresh
      }
    }, 2 * 60 * 1000); // 2 minutes

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(refreshInterval);
    };
  }, []);

  const loadCompanies = async (showLoadingIndicator = true) => {
    if (showLoadingIndicator) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError('');
    
    try {
      // Validate configuration first
      companyService.validateConfiguration();
      const companiesData = await companyService.getCompanies();
      
      // Handle the response structure - companiesData is an object with data property
      const companiesArray = companiesData.data || companiesData;
      
      setCompanies(companiesArray);
    } catch (err) {
      console.error('Error loading companies:', err);
      if (err.message.includes('Missing required environment variables')) {
        setError(`Configuration Error: ${err.message}. Please check your .env file.`);
      } else if (err.message.includes('Failed to fetch companies: 404')) {
        // Try to initialize the file for first-time setup
        try {
          const initializedCompanies = await companyService.initializeCompaniesFile();
          setCompanies(initializedCompanies);
        } catch (initError) {
          setError(`Failed to initialize companies: ${initError.message}`);
        }
      } else if (err.message.includes('403') || err.message.includes('Server failed to authenticate')) {
        // Show a user-friendly message for authentication errors
        setError('Temporary authentication issue. Please try refreshing again in a moment.');
        console.warn('Companies page: Authentication error - user should retry');
      } else {
        setError(`Failed to load companies: ${err.message}`);
      }
    } finally {
      if (showLoadingIndicator) {
        setLoading(false);
      } else {
        setRefreshing(false);
      }
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }));
    setPage(0); // Reset to first page when filtering
  };

  const handleCreateCompany = () => {
    navigate('/company/new');
  };

  const handleEditCompany = (companyId) => {
    navigate(`/company/${companyId}`);
  };

  const handleDeleteClick = (company) => {
    setDeleteDialog({ open: true, company });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.company) return;

    setDeleting(true);
    try {
      await companyService.deleteCompany(deleteDialog.company.companyId);
      await loadCompanies(); // Reload the list
      setDeleteDialog({ open: false, company: null });
    } catch (err) {
      console.error('Error deleting company:', err);
      // Check if the error is due to associations
      if (err.message.includes('contact(s) associated') || 
          err.message.includes('opportunities associated') || 
          err.message.includes('support contracts associated')) {
        setErrorMessage(err.message);
        setErrorDialogOpen(true);
      } else {
        setError(`Failed to delete company: ${err.message}`);
      }
    } finally {
      setDeleting(false);
      setDeleteDialog({ open: false, company: null });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, company: null });
  };

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
    setErrorMessage('');
  };

  // Filter and sort companies
  const filteredAndSortedCompanies = React.useMemo(() => {
    return companies
      .filter(company => {
        const matchesCompanyName = !filters.companyName || 
          (company.companyName || '').toLowerCase().includes(filters.companyName.toLowerCase());
        
        const matchesMainPhone = !filters.mainPhone || 
          (company.mainPhone || '').toLowerCase().includes(filters.mainPhone.toLowerCase());
        
        const matchesEmail = !filters.email || 
          (company.email || '').toLowerCase().includes(filters.email.toLowerCase());
        
        const matchesCreatedBy = !filters.createdByEmail || 
          (company.createdByEmail || '').toLowerCase().includes(filters.createdByEmail.toLowerCase());
        
        const matchesCreatedDate = !filters.createdDate || 
          (company.createdDate || '').toLowerCase().includes(filters.createdDate.toLowerCase());

        return matchesCompanyName && matchesMainPhone && matchesEmail && 
               matchesCreatedBy && matchesCreatedDate;
      })
      .sort((a, b) => {
        const aValue = (a[orderBy] || '').toLowerCase();
        const bValue = (b[orderBy] || '').toLowerCase();
        
        if (order === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      });
  }, [companies, filters, orderBy, order]);

  // Paginate the results
  const paginatedCompanies = filteredAndSortedCompanies.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', pr: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
            <CompaniesIcon sx={{ mr: 2 }} />
            Companies
          </Typography>
          {refreshing && (
            <Typography variant="body2" color="text.secondary">
              Refreshing data...
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Refresh Button */}
          <IconButton
            onClick={() => loadCompanies(true)}
            disabled={loading || refreshing}
            title="Refresh data"
            sx={{ 
              color: 'primary.main',
              animation: refreshing ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': {
                  transform: 'rotate(0deg)',
                },
                '100%': {
                  transform: 'rotate(360deg)',
                },
              }
            }}
          >
            <RefreshIcon />
          </IconButton>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateCompany}
          >
            Create Company
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={3}>
        {/* Search Filters - Aligned with table columns */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          {/* Mobile filters */}
          {isMobile && (
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Company Name"
                value={filters.companyName}
                onChange={handleFilterChange('companyName')}
                fullWidth
              />
            </Stack>
          )}
          
          {filteredAndSortedCompanies.length !== companies.length && (
            <Box sx={{ mt: 2 }}>
              <Chip 
                label={`Showing ${filteredAndSortedCompanies.length} of ${companies.length} companies`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          )}
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              {/* Search filters row - only show on desktop */}
              {!isMobile && (
                <TableRow key="filter-row">
                  <TableCell sx={{ borderBottom: 'none', pb: 1, width: '25%' }}>
                    <TextField
                      size="small"
                      placeholder="Search Company Name"
                      value={filters.companyName}
                      onChange={handleFilterChange('companyName')}
                      fullWidth
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none', pb: 1, width: '15%' }}>
                    <TextField
                      size="small"
                      placeholder="Search Main Phone"
                      value={filters.mainPhone}
                      onChange={handleFilterChange('mainPhone')}
                      fullWidth
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none', pb: 1, width: '20%' }}>
                    <TextField
                      size="small"
                      placeholder="Search Email"
                      value={filters.email}
                      onChange={handleFilterChange('email')}
                      fullWidth
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none', pb: 1, width: '15%' }}>
                    <TextField
                      size="small"
                      placeholder="Search Created By"
                      value={filters.createdByEmail}
                      onChange={handleFilterChange('createdByEmail')}
                      fullWidth
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none', pb: 1, width: '12%' }}>
                    <TextField
                      size="small"
                      placeholder="Search Created Date"
                      value={filters.createdDate}
                      onChange={handleFilterChange('createdDate')}
                      fullWidth
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none', pb: 1, width: '13%', minWidth: '120px' }}>
                    {/* Actions column - no filter */}
                  </TableCell>
                </TableRow>
              )}
              
              {/* Column headers row */}
              <TableRow key="header-row">
                <TableCell sx={{ width: '25%' }}>
                  <TableSortLabel
                    active={orderBy === 'companyName'}
                    direction={orderBy === 'companyName' ? order : 'asc'}
                    onClick={() => handleRequestSort('companyName')}
                  >
                    Company Name
                  </TableSortLabel>
                </TableCell>
                {!isMobile && (
                  <>
                    <TableCell sx={{ width: '15%' }}>
                      <TableSortLabel
                        active={orderBy === 'mainPhone'}
                        direction={orderBy === 'mainPhone' ? order : 'asc'}
                        onClick={() => handleRequestSort('mainPhone')}
                      >
                        Main Phone
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: '20%' }}>
                      <TableSortLabel
                        active={orderBy === 'email'}
                        direction={orderBy === 'email' ? order : 'asc'}
                        onClick={() => handleRequestSort('email')}
                      >
                        Email
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: '15%' }}>
                      <TableSortLabel
                        active={orderBy === 'createdByEmail'}
                        direction={orderBy === 'createdByEmail' ? order : 'asc'}
                        onClick={() => handleRequestSort('createdByEmail')}
                      >
                        Created By
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: '12%' }}>
                      <TableSortLabel
                        active={orderBy === 'createdDate'}
                        direction={orderBy === 'createdDate' ? order : 'asc'}
                        onClick={() => handleRequestSort('createdDate')}
                      >
                        Created Date
                      </TableSortLabel>
                    </TableCell>
                  </>
                )}
                <TableCell align="right" sx={{ width: '13%', minWidth: '120px' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCompanies.map((company, index) => {
                const rowKey = company.companyId || `company-${index}`;
                return (
                  <TableRow key={rowKey} hover>
                    <TableCell sx={{ width: '25%' }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {company.companyName}
                      </Typography>
                      {isMobile && (
                        <Box sx={{ mt: 1 }}>
                          {company.mainPhone && (
                            <Typography variant="body2" color="text.secondary">
                              📞 {company.mainPhone}
                            </Typography>
                          )}
                          {company.email && (
                            <Typography variant="body2" color="text.secondary">
                              ✉️ <a 
                                href={`mailto:${company.email}`}
                                style={{ 
                                  color: 'inherit', 
                                  textDecoration: 'none'
                                }}
                              >
                                {company.email}
                              </a>
                            </Typography>
                          )}
                          <Box sx={{ mt: 1 }}>
                            <UserDisplay email={company.createdByEmail} size="small" />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(company.createdDate).toLocaleDateString()}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    {!isMobile && (
                      <>
                        <TableCell sx={{ width: '15%' }}>
                          {company.mainPhone}
                        </TableCell>
                        <TableCell sx={{ width: '20%' }}>
                          <a 
                            href={`mailto:${company.email}`}
                            style={{ 
                              color: 'inherit', 
                              textDecoration: 'none'
                            }}
                          >
                            {company.email}
                          </a>
                        </TableCell>
                        <TableCell sx={{ width: '15%' }}>
                          <UserDisplay email={company.createdByEmail} />
                        </TableCell>
                        <TableCell sx={{ width: '12%' }}>
                          {new Date(company.createdDate).toLocaleDateString()}
                        </TableCell>
                      </>
                    )}
                    <TableCell align="right" sx={{ width: '13%', minWidth: '120px' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditCompany(company.companyId)}
                          title="Edit Company"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(company)}
                          title="Delete Company"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {paginatedCompanies.length === 0 && (
                <TableRow key="empty-state">
                  <TableCell colSpan={isMobile ? 2 : 6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {filteredAndSortedCompanies.length === 0 && companies.length > 0
                        ? 'No companies match your search criteria'
                        : 'No companies found. Create your first company to get started.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {filteredAndSortedCompanies.length > 0 && (
          <TablePagination
            component="div"
            count={filteredAndSortedCompanies.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the company "{deleteDialog.company?.companyName}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog for Association Errors */}
      <Dialog open={errorDialogOpen} onClose={handleErrorDialogClose}>
        <DialogTitle>Cannot Delete Company</DialogTitle>
        <DialogContent>
          <Typography color="error">
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleErrorDialogClose} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Companies; 
